import { useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Eye, EyeOff, KeyRound, Save, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";
import ScheduleEmailSettings from "@/components/ScheduleEmailSettings";
import XeroSettings from "@/components/XeroSettings";
import CreditApplySettings from "@/components/CreditApplySettings";
import InvoiceSyncSettings from "@/components/InvoiceSyncSettings";
import GoogleContactsSettings from "@/components/GoogleContactsSettings";
import ReceiptsSettings from "@/components/ReceiptsSettings";
import IntakeFormsSettings from "@/components/IntakeFormsSettings";
import { OnboardingDiagnostics } from "@/components/IntakeFormsSettings";
import OnboardingTemplatesSettings from "@/components/OnboardingTemplatesSettings";
import ProfileOptionsSettings from "@/components/ProfileOptionsSettings";
import NoteTemplateBuilder from "@/pages/NoteTemplateBuilder";
import DataMigrationPanel from "@/components/DataMigrationPanel";
import NameSplitBackfillPanel from "@/components/NameSplitBackfillPanel";
import StripeSettings from "@/components/StripeSettings";
import BrevoSettings from "@/components/BrevoSettings";
import ClickSendSettings from "@/components/ClickSendSettings";
import MetaIntegrationSettings from "@/components/MetaIntegrationSettings";
import MetaLeadAdsSettings from "@/components/MetaLeadAdsSettings";
import RemindersSettings from "@/components/RemindersSettings";
import MilestoneRules from "@/components/MilestoneRules";
import UnpaidReportSettings from "@/components/UnpaidReportSettings";
import OpenRouterSettings from "@/components/OpenRouterSettings";
import SoapTemplatesSettings from "@/components/SoapTemplatesSettings";
import ExerciseProgramTemplatesCard from "@/components/ExerciseProgramTemplatesCard";
import SectionCategoriesCard from "@/components/SectionCategoriesCard";
import AppointmentPathwayMapCard from "@/components/AppointmentPathwayMapCard";
import DnaDispatchSettings from "@/components/DnaDispatchSettings";
import SbnSettings from "@/components/SbnSettings";
import SignatureSettings from "@/components/SignatureSettings";
import HandwritingSettings from "@/components/HandwritingSettings";
import LetterTemplatesSettings from "@/components/LetterTemplatesSettings";
import GDriveImportRootSettings from "@/components/GDriveImportRootSettings";
import ClinicAppearance from "@/components/appearance/ClinicAppearance";
import { Palette } from "lucide-react";

export default function Settings() {
  const { status, refreshStatus, user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["connection", "scheduling", "payments", "contacts", "clinical", "onboarding", "ai", "appearance"];
  const initialTab = VALID_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "connection";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && VALID_TABS.includes(t) && t !== tab) setTab(t);
  }, [searchParams]);
  function handleTabChange(v) {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "connection") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  }
  const [userId, setUserId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [account, setAccount] = useState(null);
  // Refs for the sync-status-banner deep-link (``?reconnect=acuity``):
  // when Acuity credentials fail the live probe, we scroll the
  // credentials card into view + focus the API-key input so the
  // operator lands with their cursor on the exact thing they need
  // to fix. A ref guard defuses React 18 strict-mode double-invoke.
  const apiKeyInputRef = useRef(null);
  const acuityCardRef = useRef(null);
  const acuityDeepLinkFired = useRef(false);

  useEffect(() => {
    if (acuityDeepLinkFired.current) return;
    if (searchParams.get("reconnect") !== "acuity") return;
    // Ensure the Connection tab is active — banner target is inside it.
    if (tab !== "connection") setTab("connection");
    acuityDeepLinkFired.current = true;
    // Strip the param immediately so a back-nav / re-render doesn't
    // re-trigger the scroll + focus dance.
    const next = new URLSearchParams(searchParams);
    next.delete("reconnect");
    setSearchParams(next, { replace: true });
    // Wait one frame so the tab content is mounted before scrolling.
    requestAnimationFrame(() => {
      acuityCardRef.current?.scrollIntoView({
        behavior: "smooth", block: "start",
      });
      // Then focus the input after the smooth-scroll starts.
      setTimeout(() => {
        apiKeyInputRef.current?.focus();
        apiKeyInputRef.current?.select?.();
      }, 250);
    });
    toast.info("Reconnect Acuity", {
      description: "Paste a fresh API key below, then click Save credentials.",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (status?.user_id) setUserId(status.user_id);
  }, [status?.user_id]);

  async function save() {
    if (!userId.trim() || !apiKey.trim()) {
      toast.error("Both User ID and API Key are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/settings", { user_id: userId.trim(), api_key: apiKey.trim() });
      toast.success("Credentials saved");
      setApiKey("");
      await refreshStatus();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    setTesting(true);
    setAccount(null);
    try {
      const body =
        userId && apiKey ? { user_id: userId.trim(), api_key: apiKey.trim() } : null;
      const r = await api.post("/settings/test", body);
      if (r.data.ok) {
        toast.success("Connected to Acuity", {
          description: r.data.account?.name || r.data.message,
        });
        setAccount(r.data.account || null);
        await refreshStatus();
      } else {
        toast.error("Connection failed", { description: r.data.message });
      }
    } catch (e) {
      toast.error("Connection failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          Connection
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
          Settings
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-600">
          Manage your Acuity connection plus all clinic integrations. Grouped
          into tabs so you can jump to what you need without scrolling.
        </p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange} className="space-y-6" data-testid="settings-tabs">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-zinc-100 p-1">
          <TabsTrigger value="connection" data-testid="settings-tab-connection">Connection</TabsTrigger>
          <TabsTrigger value="scheduling" data-testid="settings-tab-scheduling">Scheduling &amp; Reminders</TabsTrigger>
          <TabsTrigger value="payments" data-testid="settings-tab-payments">Payments &amp; Invoicing</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="settings-tab-contacts">Contacts &amp; Messaging</TabsTrigger>
          <TabsTrigger value="clinical" data-testid="settings-tab-clinical">Clinical</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="settings-tab-onboarding">Onboarding &amp; Forms</TabsTrigger>
          <TabsTrigger value="ai" data-testid="settings-tab-ai">AI &amp; Letters</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="settings-tab-appearance">Appearance</TabsTrigger>
        </TabsList>

        <TabsContent value="connection" className="space-y-6 mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card ref={acuityCardRef} className="lg:col-span-2 p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none bg-zinc-50">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <KeyRound className="h-3.5 w-3.5" /> Acuity credentials
          </div>

          <div className="mt-6 space-y-5">
            <div>
              <Label htmlFor="user_id" className="text-zinc-700">
                User ID
              </Label>
              <Input
                id="user_id"
                data-testid="settings-user-id"
                placeholder="e.g. 12345678"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="mt-1.5 font-mono-num bg-white border-zinc-300 focus-visible:ring-zinc-950"
              />
              <p className="mt-1.5 text-xs text-zinc-500">
                Numeric identifier shown next to your API Key in Acuity.
              </p>
            </div>

            <div>
              <Label htmlFor="api_key" className="text-zinc-700">
                API Key
              </Label>
              <div className="mt-1.5 relative">
                <Input
                  id="api_key"
                  ref={apiKeyInputRef}
                  data-testid="settings-api-key"
                  type={showKey ? "text" : "password"}
                  placeholder={status.configured ? "•••••••••••••• (saved)" : "Paste API key"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="font-mono-num bg-white border-zinc-300 pr-10 focus-visible:ring-zinc-950"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  data-testid="toggle-api-key-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
                  aria-label="Toggle API key visibility"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-zinc-500">
                Leave blank to keep the previously saved key.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                onClick={save}
                disabled={saving}
                data-testid="settings-save-btn"
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
              >
                <Save className="h-4 w-4 mr-1.5" />
                {saving ? "Saving…" : "Save credentials"}
              </Button>
              <Button
                onClick={testConnection}
                disabled={testing}
                data-testid="settings-test-btn"
                variant="outline"
                className="border-zinc-300 hover:bg-zinc-100"
              >
                <ShieldCheck className="h-4 w-4 mr-1.5" />
                {testing ? "Testing…" : "Test connection"}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Status
          </div>

          <div className="mt-4 space-y-3 text-sm">
            <Row label="Connection" value={status.configured ? "Configured" : "Not configured"} ok={status.configured} />
            <Row label="User ID" value={status.user_id || "—"} />
            <Row
              label="Last verified"
              value={
                status.last_verified
                      ? fmtDateTime(status.last_verified)
                  : "Never"
              }
            />
            {account && (
              <>
                <div className="border-t border-zinc-100 pt-3 text-2xs uppercase tracking-wider text-zinc-500">
                  Account
                </div>
                <Row label="Name" value={account.name || "—"} />
                <Row label="Email" value={account.email || "—"} />
                <Row label="Currency" value={account.currency || "—"} />
                <Row label="Timezone" value={account.timezone || "—"} />
              </>
            )}
          </div>
        </Card>
      </div>

          <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              <Sparkles className="h-3.5 w-3.5" /> Where to find your API credentials
            </div>
            <ol className="mt-4 space-y-2 text-sm text-zinc-700 list-decimal pl-5">
              <li>Sign in to Acuity Scheduling at <span className="font-mono-num">acuityscheduling.com</span>.</li>
              <li>Click <span className="font-medium">Integrations</span> in the left sidebar.</li>
              <li>Scroll to the <span className="font-medium">API</span> section and click <span className="font-medium">View Credentials</span>.</li>
              <li>Copy the <span className="font-medium">User ID</span> (numeric) and <span className="font-medium">API Key</span> and paste above.</li>
            </ol>
          </Card>

          <GDriveImportRootSettings />
        </TabsContent>

        <TabsContent value="scheduling" className="space-y-6 mt-0">
          <ScheduleEmailSettings />
          <RemindersSettings />
          <MilestoneRules />
          <DnaDispatchSettings />
        </TabsContent>

        <TabsContent value="payments" className="space-y-6 mt-0">
          <XeroSettings />
          <StripeSettings />
          <InvoiceSyncSettings />
          <CreditApplySettings />
          <ReceiptsSettings />
          <UnpaidReportSettings />
        </TabsContent>

        <TabsContent value="contacts" className="space-y-6 mt-0">
          <BrevoSettings />
          <ClickSendSettings />
          <MetaIntegrationSettings />
          <MetaLeadAdsSettings />
          <GoogleContactsSettings />
        </TabsContent>

        <TabsContent value="clinical" className="space-y-6 mt-0">
          <SbnSettings />
          <SoapTemplatesSettings />
          <AppointmentPathwayMapCard />
          <ExerciseProgramTemplatesCard />
          <SectionCategoriesCard />
          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
            <div className="mb-3">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Note templates</div>
              <div className="text-sm text-zinc-600">
                Build reusable treatment-note templates. Add sections + typed fields (including body-chart and outcome-measure), set conditional logic, then publish. New notes created against a template snapshot its schema so future edits never rewrite past documentation.
              </div>
            </div>
            <NoteTemplateBuilder />
          </Card>
          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
            <div className="mb-3">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Data migration</div>
              <div className="text-sm text-zinc-600">
                Backfill the new Clinical Summary / Alerts / Medical History / Medications / Allergies modules from past assessment answers and intake submissions.
              </div>
            </div>
            <DataMigrationPanel />
            <NameSplitBackfillPanel />
          </Card>
        </TabsContent>

        <TabsContent value="onboarding" className="space-y-6 mt-0">
          <OnboardingTemplatesSettings />
          <ProfileOptionsSettings />
          <IntakeFormsSettings />
          <OnboardingDiagnostics />
          <Card className="p-4 rounded-md border border-zinc-200">
            <div className="text-sm font-medium text-zinc-900 mb-1">Prefill Coverage</div>
            <div className="text-xs text-zinc-500 mb-2">
              Audit every intake canonical field against the assessment schemas to find gaps
              where captured data has nowhere to land.
            </div>
            <Link
              to="/admin/prefill-coverage"
              className="inline-flex items-center gap-1.5 text-xs text-zinc-900 border border-zinc-300 rounded px-2 py-1 hover:bg-zinc-50"
              data-testid="settings-prefill-coverage-link"
            >
              Open Prefill Coverage report →
            </Link>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6 mt-0">
          <OpenRouterSettings />
          <HandwritingSettings />
          <SignatureSettings />
          <ReplyTemplatesLink />
          <LetterTemplatesSettings />
        </TabsContent>

        <TabsContent value="appearance" className="space-y-6 mt-0">
          <MyAppearanceLink />
          {user?.role === "owner" ? (
            <ClinicAppearance />
          ) : (
            <Card className="p-6 rounded-lg border border-zinc-200 shadow-none text-sm text-zinc-600" data-testid="clinic-appearance-owner-only">
              Only the owner can change the clinic-wide theme.
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 pb-2 last:border-0">
      <span className="text-zinc-500">{label}</span>
      <span
        className={
          "font-mono-num " +
          (ok === undefined ? "text-zinc-950" : ok ? "text-emerald-700" : "text-amber-700")
        }
      >
        {value}
      </span>
    </div>
  );
}


function MyAppearanceLink() {
  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Just for you</div>
        <h3 className="mt-1 font-display text-2xl text-zinc-950">My appearance</h3>
        <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
          Pick your own theme, colours, density and text size. Only your account is affected.
        </p>
      </div>
      <Link
        to="/appearance"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-950 text-white text-sm hover:bg-zinc-800 transition-colors"
        data-testid="settings-my-appearance-link"
      >
        <Palette className="h-4 w-4" /> Open my appearance
      </Link>
    </Card>
  );
}

function ReplyTemplatesLink() {
  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            AI reply templates
          </div>
          <h3 className="mt-1 font-display text-2xl text-zinc-950">Reply templates</h3>
          <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Manage the SMS and email snippets that appear as one-click chips inside
            the Draft AI Reply dialog. Tag templates with a <b>service-of-interest</b>
            (e.g. <code>exercise-physiology</code>, <code>clinical-pilates</code>)
            to auto-rank them first for matching enquiries.
          </p>
        </div>
        <a
          href="/settings/reply-templates"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-950 text-white text-sm hover:bg-zinc-800 transition-colors"
          data-testid="reply-templates-open-btn"
        >
          Manage templates →
        </a>
      </div>
    </Card>
  );
}
